import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, Percent, DollarSign, X } from "lucide-react";

const QuoteSummary = ({
  lineItems,
  subtotal,
  totalAcres,
  polygons,
  selectedCustomer,
  propertyAddress,
  parcelPin,
  requireFullPayment,
  onRequireFullPaymentChange,
  discountType,
  discountValue,
  onDiscountTypeChange,
  onDiscountValueChange,
  pricingMode,
  onPricingModeChange,
  lumpSumAmount,
  onLumpSumAmountChange,
}) => {
  const isLumpSum = pricingMode === "lump_sum";
  // Calculate discount amount
  let discountAmount = 0;
  if (discountType && discountValue) {
    if (discountType === "percentage") {
      discountAmount = subtotal * (discountValue / 100);
    } else if (discountType === "fixed") {
      discountAmount = discountValue;
    }
  }
  
  const discountedSubtotal = subtotal - discountAmount;
  const ccFee = discountedSubtotal * 0.03;
  const totalWithFee = discountedSubtotal + ccFee;
  const isOverThreshold = totalWithFee > 3000;
  // Always allow admin to choose 50/50 deposit if they uncheck "Require 100% upfront".
  // Previously this was gated to quotes > $3000; relaxed so admin can adjust on any quote.
  const showDepositOption = !requireFullPayment;
  const deposit = totalWithFee * 0.5;

  const handleAddDiscount = (type) => {
    onDiscountTypeChange(type);
    onDiscountValueChange(0);
  };

  const handleRemoveDiscount = () => {
    onDiscountTypeChange(null);
    onDiscountValueChange(null);
  };

  return (
    <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm sticky top-24">
      <CardHeader className="border-b border-zinc-100 px-6 py-4 bg-[#00341d]">
        <CardTitle className="font-heading text-lg uppercase tracking-tight text-white">Quote Summary</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Pricing mode toggle — itemized vs lump sum */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="pricing-mode-toggle">
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="lump-sum-toggle" className="text-sm font-medium text-zinc-700 cursor-pointer">
              Lump Sum Pricing
            </Label>
            <Switch
              id="lump-sum-toggle"
              checked={isLumpSum}
              onCheckedChange={(v) => onPricingModeChange(v ? "lump_sum" : "itemized")}
              data-testid="lump-sum-toggle"
            />
          </div>
          {isLumpSum ? (
            <div>
              <Label htmlFor="lump-sum-amount" className="text-xs text-zinc-500 mb-1 block">
                Lump Sum Price ($)
              </Label>
              <Input
                id="lump-sum-amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={lumpSumAmount || ""}
                onChange={(e) => onLumpSumAmountChange(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="bg-white"
                data-testid="lump-sum-amount-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
                Customer sees line items as scope only — no individual prices. One total at the bottom.
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Customer sees each line item with quantity, unit price, and total.
            </p>
          )}
        </div>

        {totalAcres > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-xs text-green-600 uppercase tracking-wider">Total Area</p>
            <p className="text-2xl font-bold text-green-800">{totalAcres.toFixed(2)} acres</p>
            <p className="text-xs text-green-600">{polygons.length} area{polygons.length > 1 ? "s" : ""}</p>
          </div>
        )}
        
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Items</span>
            <span className="font-medium">{lineItems.filter((i) => !i.is_section_header).length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-700">Subtotal</span>
            <span className="font-semibold">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          
          {/* Discount Section */}
          {discountType ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">Discount</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveDiscount}
                  className="h-6 w-6 p-0 text-green-600 hover:text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Input
                    type="number"
                    step={discountType === "percentage" ? "1" : "0.01"}
                    min="0"
                    max={discountType === "percentage" ? "100" : undefined}
                    value={discountValue || ""}
                    onChange={(e) => onDiscountValueChange(parseFloat(e.target.value) || 0)}
                    className="pr-8 h-9 text-green-800 bg-white"
                    placeholder={discountType === "percentage" ? "10" : "100.00"}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                    {discountType === "percentage" ? "%" : "$"}
                  </span>
                </div>
              </div>
              {discountAmount > 0 && (
                <p className="text-sm text-green-700 mt-2">
                  -{discountType === "percentage" ? `${discountValue}%` : ""} (${discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                </p>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddDiscount("percentage")}
                className="flex-1 text-xs h-8 text-green-700 border-green-300 hover:bg-green-50"
              >
                <Percent className="w-3 h-3 mr-1" /> % Discount
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddDiscount("fixed")}
                className="flex-1 text-xs h-8 text-green-700 border-green-300 hover:bg-green-50"
              >
                <DollarSign className="w-3 h-3 mr-1" /> $ Discount
              </Button>
            </div>
          )}
          
          {/* Show discounted subtotal if discount applied */}
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm text-green-700">
              <span>After Discount</span>
              <span className="font-medium">${discountedSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">CC Processing Fee (3%)</span>
            <span className="text-zinc-600">${ccFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="border-t border-zinc-200 pt-3">
            <div className="flex justify-between text-lg">
              <span className="font-bold text-[#00341d]">Total</span>
              <span className="font-bold text-[#00341d]">${totalWithFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
          <p className="text-sm font-medium text-amber-800 mb-2">Payment Terms</p>

          {/* Always show the toggle so admin can adjust payment terms on any quote */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-amber-200">
            <Label htmlFor="full-payment" className="text-sm text-amber-700 cursor-pointer">
              Require 100% upfront
            </Label>
            <Switch
              id="full-payment"
              checked={requireFullPayment}
              onCheckedChange={onRequireFullPaymentChange}
              data-testid="require-full-payment-toggle"
            />
          </div>

          {showDepositOption ? (
            <>
              <p className="text-sm text-amber-700">50% Deposit: ${deposit.toLocaleString(undefined, { minimumFractionDigits: 2 })} on signing</p>
              <p className="text-sm text-amber-700">50% Balance: ${(totalWithFee - deposit).toLocaleString(undefined, { minimumFractionDigits: 2 })} on completion</p>
            </>
          ) : (
            <p className="text-sm text-amber-700">100% (${totalWithFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}) due on signing</p>
          )}
        </div>

        {selectedCustomer && (
          <div className="border-t border-zinc-200 pt-4 mt-4">
            <p className="text-sm text-zinc-500 mb-2">Customer</p>
            <p className="font-medium text-zinc-900">{selectedCustomer.name}</p>
            {propertyAddress && (
              <p className="text-sm text-zinc-500 mt-1 flex items-start gap-1">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {propertyAddress}
              </p>
            )}
            {parcelPin && (
              <p className="text-xs text-zinc-400 mt-1">PIN: {parcelPin}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteSummary;
